import React, { useState, useRef, useEffect, useCallback } from "react";
import moment from "moment";

import { useMediaQuery } from "../../hooks/useMediaQuery";
import { Carousel } from "../Carousel/Carousel";
import { GameCard } from "../GameCard/GameCard";
import Button from "../Button/Button3";

import "./GameFilter.css";
import CarmenImage from "../../assets/images/carmen.png";
import resolveMediaUrl from "../../utils/resolveMediaUrl";

export const GameFilter = ({ games = [], loading = false }) => {
  const isSmall = useMediaQuery("(max-width: 480px)");
  const variant = isSmall ? "medium" : "large";

  /* ---------------- state ---------------- */
  const [activeFilters, setActive] = useState({ level: null, date: null, place: null });
  const [filterType, setFilterType] = useState("date");
  const [placeOptions, setPlaceOptions] = useState([]);
  const [week, setWeek] = useState({
    start: moment().startOf("isoWeek"),
    end: moment().endOf("isoWeek"),
  });

  /* derive unique places whenever games change */
  useEffect(() => {
    const unique = [...new Set(games.map((g) => g.stadium?.name || "Unknown Stadium"))];
    setPlaceOptions(unique);
  }, [games]);

  /* ---------- helper to change week ---------- */
  const changeWeek = (dir) =>
    setWeek((w) => ({
      start: moment(w.start).add(dir, "week"),
      end: moment(w.end).add(dir, "week"),
    }));

  /* ---------- build week day objects ---------- */
  const weekDays = [];
  let d = week.start.clone();
  while (d.isSameOrBefore(week.end)) {
    weekDays.push({
      full: d.format("dddd, DD.MM.YYYY"),
      day: d.format("dddd"),
      num: d.format("DD.MM.YYYY"),
    });
    d.add(1, "day");
  }

  /* ---------- filtering ---------- */
  const filteredGames = games.filter((g) => {
    const matchesLevel = !activeFilters.level || g.type === activeFilters.level;
    const matchesDate =
      !activeFilters.date || moment(g.date).format("dddd, DD.MM.YYYY") === activeFilters.date;
    const matchesPlace =
      !activeFilters.place || g.stadium?.name === activeFilters.place;
    return matchesLevel && matchesDate && matchesPlace;
  });

  /* ---------- carousel drag + auto scroll (same as before, trimmed) ---------- */
  const carouselRef = useRef(null);
  const scrollInterval = useRef(null);
  const carouselKey =
    filterType === "date"
      ? `date-${week.start.format("YYYY-MM-DD")}-${week.end.format("YYYY-MM-DD")}`
      : `${filterType}-${placeOptions.length}`;

  const stopAuto = useCallback(() => {
    if (scrollInterval.current) {
      clearInterval(scrollInterval.current);
      scrollInterval.current = null;
    }
  }, []);

  const resetCarouselPosition = useCallback(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;
    carousel.scrollTo({ left: 0, behavior: "auto" });
  }, []);

  const getCarouselMetrics = useCallback(() => {
    const carousel = carouselRef.current;
    if (!carousel) return null;

    const maxScrollLeft = Math.max(0, carousel.scrollWidth - carousel.clientWidth);
    const track = carousel.querySelector(".carousel-track");
    const firstItem = track?.firstElementChild;

    let step = Math.max(Math.round(carousel.clientWidth * 0.75), 160);
    if (track && firstItem) {
      const trackStyles = window.getComputedStyle(track);
      const gap =
        parseFloat(trackStyles.columnGap || trackStyles.gap || "0") || 0;
      step = Math.max(Math.round(firstItem.getBoundingClientRect().width + gap), 1);
    }

    return {
      carousel,
      maxScrollLeft,
      step,
    };
  }, []);

  const scrollRight = useCallback(() => {
    const metrics = getCarouselMetrics();
    if (!metrics || metrics.maxScrollLeft <= 0) return;

    const { carousel, maxScrollLeft, step } = metrics;
    const currentScrollLeft = Math.max(0, carousel.scrollLeft);
    const nextScrollLeft = currentScrollLeft + step;

    if (currentScrollLeft >= maxScrollLeft - 4) {
      carousel.scrollTo({ left: 0, behavior: "auto" });
      return;
    }

    if (nextScrollLeft >= maxScrollLeft - 4) {
      carousel.scrollTo({ left: maxScrollLeft, behavior: "smooth" });
      return;
    }

    carousel.scrollTo({ left: nextScrollLeft, behavior: "smooth" });
  }, [getCarouselMetrics]);

  const startAuto = useCallback(() => {
    stopAuto();
    const metrics = getCarouselMetrics();
    if (!metrics || metrics.maxScrollLeft <= 0) return;

    scrollInterval.current = setInterval(scrollRight, 3000);
  }, [getCarouselMetrics, scrollRight, stopAuto]);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      resetCarouselPosition();
      startAuto();
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      stopAuto();
    };
  }, [carouselKey, resetCarouselPosition, startAuto, stopAuto]);

  /* ---------- option click ---------- */
  const toggle = (cat, val) =>
    setActive((p) => ({ ...p, [cat]: p[cat] === val ? null : val }));

  /* ---------- render filter buttons ---------- */
  const renderOptions = () => {
    if (filterType === "date") {
      return (
        <>
          <Carousel key={carouselKey} ref={carouselRef}>
            {weekDays.map((w) => (
              <Button
                key={w.full}
                variant={variant}
                primaryText={w.day}
                secondaryText={w.num}
                onClick={() => toggle("date", w.full)}
                styleType={activeFilters.date === w.full ? "active" : "default"}
                onMouseEnter={stopAuto}
                onMouseLeave={startAuto}
              />
            ))}
          </Carousel>
          <div className="carousel-header">
            <button className="carousel-nav left" onClick={() => changeWeek(-1)}>
              &lt;
            </button>
            <p className="carousel-title">
              {week.start.format("DD.MM.YYYY")} – {week.end.format("DD.MM.YYYY")}
            </p>
            <button className="carousel-nav right" onClick={() => changeWeek(1)}>
              &gt;
            </button>
          </div>
        </>
      );
    }

    if (filterType === "level") {
      const levels = ["Beginner", "Rising", "Champion"];
      return (
        <Carousel key={carouselKey} ref={carouselRef} centerItems={!isSmall}>
          {levels.map((lvl) => (
            <Button
              key={lvl}
              variant={variant}
              primaryText={lvl}
              onClick={() => toggle("level", lvl)}
              styleType={activeFilters.level === lvl ? "active" : "default"}
              onMouseEnter={stopAuto}
              onMouseLeave={startAuto}
            />
          ))}
        </Carousel>
      );
    }

    /* place filter */
    return (
      <Carousel key={carouselKey} ref={carouselRef} centerItems={!isSmall}>
        {placeOptions.map((p) => (
          <Button
            key={p}
            variant={variant}
            primaryText={p}
            onClick={() => toggle("place", p)}
            styleType={activeFilters.place === p ? "active" : "default"}
            onMouseEnter={stopAuto}
            onMouseLeave={startAuto}
          />
        ))}
      </Carousel>
    );
  };

  /* ---------- cards ---------- */
  const cards = filteredGames.map((g) => {
    const img = resolveMediaUrl(g.stadium?.stadiumPicturePath, CarmenImage);
    return (
      <GameCard
        key={g._id}
        style={isSmall ? { width: 300, height: 100 } : { width: 140, height: 174 }}
        imageSrc={img}
        gameName={g.stadium?.name}
        gameSubtitle={g.type}
        gameDay={g.date}
        gameTime={g.time}
        gameId={g._id}
      />
    );
  });

  return (
    <div className="game-filter-container">
      <p className="choose-by-title">CHOOSE BY…</p>

      <div className="filter-titles">
        {["date", "level", "place"].map((t) => (
          <span key={t} className={filterType === t ? "active" : ""} onClick={() => setFilterType(t)}>
            {t.toUpperCase()}
          </span>
        ))}
      </div>

      <div
        className="filter-options-container"
        onMouseEnter={stopAuto}
        onMouseLeave={startAuto}
        onTouchStart={stopAuto}
        onTouchEnd={startAuto}
        onTouchCancel={startAuto}
      >
        {renderOptions()}
      </div>

      {loading ? (
        <p className="no-games-message">Loading…</p>
      ) : filteredGames.length ? (
        <div className={`filtered-games ${filteredGames.length <= 5 ? "centered" : ""}`}>
          {cards}
        </div>
      ) : (
        <p className="no-games-message">No upcoming games for the selected filters.</p>
      )}
    </div>
  );
};

export default GameFilter;
